import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Switch } from '@wartek-id/switch'
import { Text } from '@wartek-id/text-v1'

import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type StepSelectSchoolProps = {
  schools: Array<PelaksanaanSchool>
  kepalaSekolahLabel?: string
  pegawaiLabel?: string
  onChangeSelectedSchools: (schools: Array<PelaksanaanSchool>) => void
  onNext: () => void
  onPrev: () => void
}

export function StepSelectSchool({
  schools,
  kepalaSekolahLabel = 'Kepala Sekolah',
  pegawaiLabel = 'Guru',
  onChangeSelectedSchools,
  onNext,
  onPrev,
}: StepSelectSchoolProps) {
  const { data: dataProfile, isLoading: isLoadingProfile } =
    useAPIKinerjaProfile({
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const isNextDisabled = schools.length === 0

  const handleChangeSchoolSwitch = (npsn: string, isChecked: boolean) => {
    onChangeSelectedSchools(
      schools.map((school) => {
        if (school.npsn === npsn) {
          school.confirmationStatus = isChecked ? 'CONFIRMED' : 'REJECTED'
        }

        return school
      })
    )
  }

  const handleNext = () => {
    if (isNextDisabled) return

    // Set the nonselected schools to be rejected
    onChangeSelectedSchools(
      schools.map((school) => {
        if (school.confirmationStatus === 'UNCONFIRMED') {
          school.confirmationStatus = 'REJECTED'
        }

        return school
      })
    )

    onNext()
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Text variant="display-sm">Konfirmasi data Anda</Text>
        <Text color="subdued">
          Setelah konfirmasi, Pengelolaan Kinerja para {pegawaiLabel} di satuan
          pendidikan Anda dapat berlanjut.
        </Text>
      </div>
      <dl className="p-4 grid grid-cols-2 gap-4 rounded border border-subdued bg-surface-subdued">
        <Text as="dt" variant="body-sm" color="subdued">
          Nama Plt.{kepalaSekolahLabel}
        </Text>
        <Text
          as="dd"
          variant="body-sm"
          className="overflow-wrap-anywhere text-right"
        >
          {isLoadingProfile ? (
            <Shimmer className="ml-auto" />
          ) : (
            dataProfile?.data?.nama || '-'
          )}
        </Text>
        {dataProfile?.data?.nuptk && (
          <>
            <Text as="dt" variant="body-sm" color="subdued">
              NUPTK
            </Text>
            <Text
              as="dd"
              variant="body-sm"
              className="overflow-wrap-anywhere text-right"
            >
              {isLoadingProfile ? (
                <Shimmer className="ml-auto" />
              ) : (
                dataProfile?.data?.nuptk
              )}
            </Text>
          </>
        )}
      </dl>
      <div className="space-y-1">
        <Text variant="action">Pilih sekolah yang menjadi kewenangan Anda</Text>
        <Text color="subdued">
          Jika bukan kewenangan Anda, maka tidak perlu dipilih.
        </Text>
      </div>
      <div className="space-y-3">
        {schools.map((school) => (
          <Switch
            key={school.npsn}
            label={school.schoolName}
            checked={school.confirmationStatus === 'CONFIRMED'}
            onCheckedChange={(isChecked) =>
              handleChangeSchoolSwitch(school.npsn, isChecked)
            }
          />
        ))}
      </div>

      <hr className="block border-b border-disabled w-full" />

      <div className="flex space-x-3 justify-end">
        <Button color="white" size="lg" onClick={onPrev}>
          Kembali
        </Button>
        <Button
          color="blue"
          size="lg"
          disabled={isNextDisabled}
          onClick={handleNext}
        >
          Lanjut
        </Button>
      </div>
    </div>
  )
}
